import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { getUserPermissions, processUserPermissions } from '../../../shared/helpers/api/mantenedores/permissions';

const SimpleApiTest = () => {
  const currentUser = useSelector(state => state.auth.user);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const testApiCall = async () => {
    if (!currentUser) {
      setError('No hay usuario conectado');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      console.log('🔄 Iniciando test de API...');
      console.log('👤 Usuario:', currentUser.email);
      
      // Llamada directa a la API
      const response = await getUserPermissions(currentUser);
      console.log('📥 Respuesta de API:', response);
      
      if (response.success) {
        const processed = processUserPermissions(response.data);
        console.log('⚙️ Datos procesados:', processed);
        setResult({ raw: response.data, processed });
      } else {
        setError(response.error);
      }
    } catch (err) {
      console.error('❌ Error en test:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>🧪 Test Simple de API</h1>
      
      {currentUser && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#f0f8ff', 
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h3>👤 Usuario Conectado:</h3>
          <p><strong>Email:</strong> {currentUser.email}</p>
          <p><strong>UID:</strong> {currentUser.uid}</p>
        </div>
      )}

      <button 
        onClick={testApiCall}
        disabled={loading || !currentUser}
        style={{
          padding: '12px 24px',
          backgroundColor: loading ? '#ccc' : '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: loading || !currentUser ? 'not-allowed' : 'pointer',
          fontSize: '14px',
          fontWeight: 'bold',
          marginBottom: '20px'
        }}
      >
        {loading ? '🔄 Probando...' : '🚀 Probar API'}
      </button>

      {!currentUser && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#fff3cd', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <strong>⚠️ Debes estar conectado para probar la API</strong>
        </div>
      )}

      {loading && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#e3f2fd', 
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h3>🔄 Ejecutando prueba...</h3>
          <p>Conectando con la API de permisos</p>
        </div>
      )}

      {error && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#ffebee', 
          borderRadius: '8px',
          marginBottom: '20px',
          color: '#c62828'
        }}>
          <h3>❌ Error:</h3>
          <p>{error}</p>
        </div>
      )}

      {result && (
        <div>
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#e8f5e8', 
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#2e7d32' }}>✅ Prueba exitosa</h3>
            <p>La API respondió correctamente</p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h3>📋 Respuesta Raw de la API:</h3>
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '15px', 
              borderRadius: '4px',
              border: '1px solid #dee2e6',
              marginBottom: '15px'
            }}>
              <h4>📊 Resumen de permisos recibidos:</h4>
              <ul>
                <li><strong>Email:</strong> {result.raw.email}</li>
                <li><strong>Total de mantenedores con permisos:</strong> {Object.keys(result.raw.permisos || {}).length}</li>
                <li><strong>Mantenedores:</strong> {Object.keys(result.raw.permisos || {}).join(', ') || 'Ninguno'}</li>
              </ul>
            </div>
            <pre style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '15px', 
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px',
              border: '1px solid #dee2e6'
            }}>
              {JSON.stringify(result.raw, null, 2)}
            </pre>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h3>⚙️ Datos Procesados:</h3>
            <pre style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '15px', 
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px',
              border: '1px solid #dee2e6'
            }}>
              {JSON.stringify(result.processed, null, 2)}
            </pre>
          </div>

          {result.processed.modules && (
            <div>
              <h3>🎯 Módulos con Acceso:</h3>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                border: '1px solid #dee2e6'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #dee2e6' }}>
                      Módulo
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                      Acceso
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6' }}>
                      Permisos
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(result.processed.modules).map(([moduleId, moduleData]) => (
                    <tr key={moduleId}>
                      <td style={{ padding: '12px', border: '1px solid #dee2e6' }}>
                        {moduleId}
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        border: '1px solid #dee2e6',
                        backgroundColor: moduleData.hasAccess ? '#d4edda' : '#f8d7da',
                        color: moduleData.hasAccess ? '#155724' : '#721c24'
                      }}>
                        {moduleData.hasAccess ? '✅ Sí' : '❌ No'}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #dee2e6', fontSize: '12px' }}>
                        {moduleData.permissions.join(', ') || 'Ninguno'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SimpleApiTest;
